/* LAV TDMS — DataTable, faithfully ported from the prototype's
   ds/DataTable.jsx (window DS). The dense register table: hairline-ruled,
   sticky header, uppercase mono labels, hover + selected row states.
   Inline styles kept verbatim. */
import { type CSSProperties, type ReactNode, useEffect, useRef } from 'react'
import { useDensity } from './responsive'

/** Значение ячейки: примитив, пригодный и для показа, и для группировки/фильтра. */
export type CellValue = string | number | boolean | null | undefined

/** Безопасное чтение ячейки по ключу колонки, когда своя render не задана. */
export function cellText(row: unknown, key: string): string {
  const v = (row as Record<string, unknown>)[key]
  return v == null ? '' : String(v)
}

export interface Column<R = unknown> {
  key: string
  header: ReactNode
  width?: number | string
  align?: 'left' | 'right' | 'center'
  mono?: boolean
  nowrap?: boolean
  sortable?: boolean
  render?: (row: R, i: number) => ReactNode
}

export function DataTable<R = unknown>({
  columns = [],
  rows = [],
  rowKey = (r: R, i: number): React.Key => (r as { id?: React.Key } | null)?.id ?? i,
  selectedKeys = null,
  onToggleRow,
  onToggleAll,
  onRowClick,
  activeKey = null,
  sort = null,
  onSort,
  compact: compactProp,
  stickyHeader = true,
  emptyText = 'Нет данных',
  style = {},
}: {
  columns?: Column<R>[]
  rows?: R[]
  rowKey?: (r: R, i: number) => React.Key
  selectedKeys?: Set<React.Key> | React.Key[] | null
  onToggleRow?: (key: React.Key, row: R) => void
  onToggleAll?: (allSelected: boolean) => void
  onRowClick?: (row: R, i: number) => void
  activeKey?: React.Key | null
  sort?: { key: string; dir: 'asc' | 'desc' } | null
  onSort?: (key: string) => void
  /** Плотность строк — общая настройка приложения; см. SmartTable. */
  compact?: boolean
  stickyHeader?: boolean
  emptyText?: ReactNode
  style?: CSSProperties
}) {
  const density = useDensity()
  const compact = compactProp ?? density.compact
  const selectable = selectedKeys != null
  const selSet = selectedKeys instanceof Set ? selectedKeys : new Set(selectedKeys || [])
  const allSelected = rows.length > 0 && rows.every((r, i) => selSet.has(rowKey(r, i)))
  const someSelected = rows.some((r, i) => selSet.has(rowKey(r, i)))
  const rh = compact ? 'var(--row-h-compact)' : 'var(--row-h)'
  return (
    <div style={{ width: '100%', overflow: 'auto', ...style }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', font: 'var(--font-table)' }}>
        <thead>
          <tr>
            {selectable ? (
              <th style={{ ...thBase(stickyHeader), width: 36, paddingLeft: 'var(--space-6)' }}>
                <CheckboxLite
                  checked={allSelected}
                  indeterminate={someSelected && !allSelected}
                  onChange={() => onToggleAll && onToggleAll(allSelected)}
                />
              </th>
            ) : null}
            {columns.map((c) => {
              const sorted = !!sort && sort.key === c.key
              return (
                <th
                  key={c.key}
                  onClick={c.sortable && onSort ? () => onSort(c.key) : undefined}
                  style={{
                    ...thBase(stickyHeader),
                    width: c.width,
                    textAlign: c.align || 'left',
                    cursor: c.sortable && onSort ? 'pointer' : 'default',
                    userSelect: 'none',
                  }}
                >
                  <span
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 'var(--space-2)',
                      justifyContent: c.align === 'right' ? 'flex-end' : 'flex-start',
                    }}
                  >
                    {c.header}
                    {c.sortable ? (
                      <span
                        style={{
                          fontSize: '8px',
                          color: sorted ? 'var(--accent-ink)' : 'var(--text-subtle)',
                          lineHeight: 1,
                        }}
                      >
                        {sorted ? (sort!.dir === 'asc' ? '▲' : '▼') : '▼'}
                      </span>
                    ) : null}
                  </span>
                </th>
              )
            })}
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 ? (
            <tr>
              <td
                colSpan={columns.length + (selectable ? 1 : 0)}
                style={{
                  padding: 'var(--space-16)',
                  textAlign: 'center',
                  color: 'var(--text-subtle)',
                  font: 'var(--font-ui)',
                  fontSize: 'var(--text-sm)',
                }}
              >
                {emptyText}
              </td>
            </tr>
          ) : (
            rows.map((r, i) => {
              const key = rowKey(r, i)
              const isSel = selSet.has(key)
              const isActive = activeKey != null && key === activeKey
              return (
                <tr
                  key={key}
                  onClick={onRowClick ? () => onRowClick(r, i) : undefined}
                  className="lav-tr"
                  style={{
                    height: rh,
                    cursor: onRowClick ? 'pointer' : 'default',
                    background: isActive ? 'var(--accent-soft)' : isSel ? 'var(--surface-active)' : 'transparent',
                    boxShadow: isActive ? 'inset 2px 0 0 var(--accent)' : 'none',
                  }}
                >
                  {selectable ? (
                    <td style={{ ...tdBase(compact), paddingLeft: 'var(--space-6)' }} onClick={(e) => e.stopPropagation()}>
                      <CheckboxLite checked={isSel} onChange={() => onToggleRow && onToggleRow(key, r)} />
                    </td>
                  ) : null}
                  {columns.map((c) => (
                    <td
                      key={c.key}
                      style={{
                        ...tdBase(compact),
                        textAlign: c.align || 'left',
                        fontFamily: c.mono ? 'var(--font-mono)' : 'inherit',
                        fontSize: c.mono ? 'var(--text-xs)' : 'var(--text-sm)',
                        color: c.mono ? 'var(--text-muted)' : 'var(--text-body)',
                        whiteSpace: c.nowrap ? 'nowrap' : undefined,
                      }}
                    >
                      {c.render ? c.render(r, i) : cellText(r, c.key)}
                    </td>
                  ))}
                </tr>
              )
            })
          )}
        </tbody>
      </table>
      {/* Копия правила из ui.css живёт здесь потому, что фон строки задан
          инлайном и общее правило без !important до него не достаёт. Условие
          указателя, соответственно, тоже нужно здесь: там оно есть, а
          действует эта строка. Нажатие — вне условия: пальцем нажимают так же.
          Селектор с tr — как у smt-row: иначе у :active и :hover одинаковый
          вес, и пока держишь кнопку, отклика не видно. */}
      <style>{`
        @media (hover: hover) and (pointer: fine) { .lav-tr:hover { background: var(--surface-hover) !important; } }
        tr.lav-tr:active { background: var(--surface-active) !important; }
      `}</style>
    </div>
  )
}

function thBase(sticky: boolean): CSSProperties {
  return {
    position: sticky ? 'sticky' : 'static',
    top: 0,
    zIndex: 1,
    textAlign: 'left',
    fontFamily: 'var(--font-mono)',
    fontSize: 'var(--text-2xs)',
    fontWeight: 'var(--weight-semibold)' as CSSProperties['fontWeight'],
    letterSpacing: 'var(--tracking-caps)',
    textTransform: 'uppercase',
    color: 'var(--text-subtle)',
    background: 'var(--surface-sunken)',
    padding: 'var(--space-4) var(--space-5)',
    borderBottom: 'var(--border-hairline) solid var(--border-strong)',
    whiteSpace: 'nowrap',
  }
}
function tdBase(compact: boolean): CSSProperties {
  return {
    padding: compact ? 'var(--space-3) var(--space-5)' : 'var(--space-4) var(--space-5)',
    borderBottom: 'var(--border-hairline) solid var(--border-subtle)',
    verticalAlign: 'middle',
  }
}
function CheckboxLite({
  checked,
  indeterminate,
  onChange,
}: {
  checked?: boolean
  indeterminate?: boolean
  onChange?: React.ChangeEventHandler<HTMLInputElement>
}) {
  const ref = useRef<HTMLInputElement>(null)
  useEffect(() => {
    if (ref.current) ref.current.indeterminate = !!indeterminate && !checked
  }, [indeterminate, checked])
  return (
    <span style={{ display: 'inline-grid', placeItems: 'center' }}>
      <input
        ref={ref}
        type="checkbox"
        checked={!!checked}
        onChange={onChange}
        style={{
          appearance: 'none',
          WebkitAppearance: 'none',
          width: 15,
          height: 15,
          margin: 0,
          background: checked || indeterminate ? 'var(--accent)' : 'var(--surface-card)',
          border: `var(--border-hairline) solid ${checked || indeterminate ? 'var(--accent)' : 'var(--border-strong)'}`,
          borderRadius: 'var(--radius-sm)',
          cursor: 'pointer',
          position: 'relative',
          display: 'grid',
          placeItems: 'center',
        }}
      />
    </span>
  )
}
